import React from 'react';
import { WixRicosEditor, IMAGE_TYPE, FILE_UPLOAD_TYPE, ToolbarType as TOOLBARS } from '@wix/ricos';
import { useApi } from '../../context/api-context/use-api';
import { getAppDefinitionId, getInstance, getIsMobile, } from '../../../common/store/base-params/base-params-selectors';
import { useConfiguration } from '../../context/configuration-context/use-configuration';
import { initEditorPlugins } from './ricos-utils';
import { getExtToolbarPluginButtons } from './external-toolbars/buttons';
import ExternalPluginToolbar from './external-toolbars/external-plugin-toolbar';
import { COMMENT_BOX_RCE_WRAPPER } from '../../../common/constants/data-hooks';
import { getCustomStyles } from './ricos-styles';
import { st, classes } from './ricos-editor.st.css';
import { getCurrentMember } from '../../../common/store/members/members-selectors';
import { DISABLED_PLUGIN_TYPES_FOR_GUESTS } from './plugins';
import classNames from 'classnames';
const DISABLED_TOOLBAR = { desktop: false, mobile: { android: false, ios: false } };
const getToolbarSettings = (plugins) => {
    const extToolbarPluginButtons = plugins ? getExtToolbarPluginButtons(plugins) : [];
    return {
        getToolbarSettings: () => [
            {
                name: TOOLBARS.SIDE,
                shouldCreate: () => DISABLED_TOOLBAR,
            },
            {
                name: TOOLBARS.FOOTER,
                shouldCreate: () => DISABLED_TOOLBAR,
            },
            { name: TOOLBARS.MOBILE, shouldCreate: () => DISABLED_TOOLBAR },
            {
                name: TOOLBARS.INSERT_PLUGIN,
                shouldCreate: () => ({ desktop: true, mobile: { android: true, ios: true } }),
                // need to provide toolbar buttons explicitly to maintain plugin order
                getButtons: () => ({
                    desktop: extToolbarPluginButtons,
                    mobile: { ios: extToolbarPluginButtons, android: extToolbarPluginButtons },
                }),
            },
        ],
    };
};
const RicosEditor = ({ autoFocus, onChange, onBusyChange, placeholder, initialContent, externalToolbarId, onAddPluginContent, pluginConfig, contentId, setRef, }) => {
    const { experiments, ricosBiParams, locale, isMobile, instance, hostStyle, appDefId, currentMember, } = useApi((state, _actions, host) => ({
        experiments: state.experiments,
        ricosBiParams: state.baseParams.biMetadata,
        locale: state.baseParams?.language,
        isMobile: getIsMobile(state),
        instance: getInstance(state),
        hostStyle: host.style,
        appDefId: getAppDefinitionId(state),
        currentMember: getCurrentMember(state),
    }));
    const { ricosTheming, screenshotMode, fileUploadSizeLimit } = useConfiguration();
    const [toolbarReady, setToolbarReady] = React.useState(false);
    const editorRef = React.useRef(null);
    const didFocus = React.useRef(false);
    const { plugins, exposedPluginButtons } = React.useMemo(() => pluginConfig?.plugins
        ? initEditorPlugins(pluginConfig.plugins, {
            disabledPlugins: currentMember ? [] : DISABLED_PLUGIN_TYPES_FOR_GUESTS,
        })
        : { plugins: [], exposedPluginButtons: [] }, [pluginConfig, currentMember]);
    const handleRef = async (ref) => {
        const focusEditor = (e) => setTimeout(() => {
            e.focus('end');
            didFocus.current = true;
        }, 100);
        if (ref) {
            editorRef.current = ref;
            setRef(ref);
            if (Object.keys(ref.getToolbarProps(TOOLBARS.INSERT_PLUGIN).buttons).length) {
                setToolbarReady(true);
            }
            if (!didFocus.current && autoFocus) {
                pluginConfig?.type === 'async'
                    ? pluginConfig.onLoadFinished().then(() => focusEditor(ref))
                    : focusEditor(ref);
            }
        }
    };
    const handleChange = (content) => {
        const contentTraits = editorRef.current?.getContentTraits();
        onChange?.(content, {
            isEmpty: contentTraits?.isEmpty ?? true,
            isContentChanged: contentTraits?.isContentChanged ?? false,
        });
    };
    const biCallbacks = {
        onPluginAdd: (pluginType) => {
            if ([IMAGE_TYPE, FILE_UPLOAD_TYPE].includes(pluginType)) {
                onAddPluginContent(pluginType === IMAGE_TYPE ? 'image' : 'file');
            }
        },
    };
    return (React.createElement("div", { "data-hook": classNames(COMMENT_BOX_RCE_WRAPPER), className: st(classes.editor, { screenshotMode: Boolean(screenshotMode) }) },
        React.createElement(WixRicosEditor, { wixExperiments: experiments, key: plugins.length + exposedPluginButtons.length, ref: handleRef, plugins: plugins, locale: locale, isMobile: isMobile, toolbarSettings: getToolbarSettings(pluginConfig?.plugins), onChange: handleChange, onBusyChange: onBusyChange, placeholder: placeholder, content: initialContent, theme: {
                palette: hostStyle.siteColors,
                ...ricosTheming?.theme,
                parentClass: classes.editor,
                customStyles: getCustomStyles(isMobile, 'editor'),
            }, instance: instance, linkSettings: {
                anchorTarget: '_blank',
                rel: { nofollow: true, sponsored: false, ugc: true },
            }, linkPanelSettings: {
                showNewTabCheckbox: false,
                showNoFollowCheckbox: false,
                showSponsoredCheckbox: false,
            }, biSettings: {
                consumer: 'wixComments',
                biCallbacks,
                platform: 'Livesite',
                defaultParams: ricosBiParams,
                postId: contentId,
                contentId,
                containerId: appDefId,
            }, visitorUploads: { folderId: 'visitor-uploads' }, fileUploadSizeLimit: fileUploadSizeLimit }),
        toolbarReady && (React.createElement(ExternalPluginToolbar, { id: externalToolbarId, buttons: editorRef.current?.getToolbarProps(TOOLBARS.INSERT_PLUGIN).buttons ?? [] }))));
};
export default RicosEditor;
//# sourceMappingURL=ricos-editor.js.map